import { AppointmentCategory } from "../../types/garage/planning";

// --- Atelier grid drag types ---

export interface JobDragPreview {
  type: "job";
  jobId: string;
  startHour: number;
  durationHours: number;
  workerId: string;
  date?: string;
  overlap?: boolean;
  mergeTarget?: boolean;
  mergeTargetJobId?: string;
}

export interface ChargeDragPreview {
  type: "charge";
  chargeId: string;
  startHour: number;
  durationHours: number;
  workerId: string;
  clientName: string;
  date?: string;
  overlap?: boolean;
}

export type GridPreview = JobDragPreview | ChargeDragPreview;

// --- Atelier grid constants ---

export const HOURS_START = 7;
export const HOURS_END = 20;
/** Pixel height of one positioning unit (kept for px math: 16px / 0.125h = 128px/h). */
export const SNAP_PX = 16;
/** Positioning granularity used for pixel math only. */
export const SNAP_HOUR = 0.125;
export const HOUR_BLOCK_HEIGHT = 128;

/**
 * Drag/resize snapping on the atelier planning: 1 cell = 1 quarter-hour.
 * (Pixel positioning still uses SNAP_HOUR/SNAP_PX above.)
 */
export const ATELIER_SNAP_HOUR = 0.25;
export const ATELIER_SNAP_PX = HOUR_BLOCK_HEIGHT * ATELIER_SNAP_HOUR; // 32px per quarter

// --- Google Calendar-style overlap layout ---

export interface OverlapLayout {
  /** 0-based column index within the overlap group */
  column: number;
  /** total number of columns in the overlap group */
  totalColumns: number;
}

/**
 * Compute overlap layout for a list of time-block items (jobs).
 * Returns a Map from item id to its OverlapLayout.
 *
 * Algorithm (same as Google Calendar):
 * 1. Sort by start time, then by duration desc (longer events first)
 * 2. Greedily assign each item to the first column where it doesn't overlap
 * 3. Items in the same overlap cluster share the same totalColumns count
 */
export function computeOverlapLayout<T extends { id: string; startHour: number; durationHours: number }>(
  items: T[],
): Map<string, OverlapLayout> {
  if (items.length === 0) return new Map();

  const sorted = [...items].sort((a, b) => a.startHour - b.startHour || b.durationHours - a.durationHours);

  // Assign columns greedily
  const colEndTimes: number[] = []; // end time of the last item in each column
  const assignments: { item: T; column: number }[] = [];

  for (const item of sorted) {
    const end = item.startHour + item.durationHours;
    let placed = false;
    for (let c = 0; c < colEndTimes.length; c++) {
      if (colEndTimes[c] <= item.startHour) {
        colEndTimes[c] = end;
        assignments.push({ item, column: c });
        placed = true;
        break;
      }
    }
    if (!placed) {
      assignments.push({ item, column: colEndTimes.length });
      colEndTimes.push(end);
    }
  }

  // Find connected clusters (items that transitively overlap)
  // and set totalColumns for each cluster
  const result = new Map<string, OverlapLayout>();

  // Group into clusters: items overlap if any pair in the cluster overlaps
  const clusters: { item: T; column: number }[][] = [];
  let currentCluster: { item: T; column: number }[] = [];
  let clusterEnd = -Infinity;

  for (const a of assignments.sort((x, y) => x.item.startHour - y.item.startHour)) {
    if (a.item.startHour >= clusterEnd && currentCluster.length > 0) {
      clusters.push(currentCluster);
      currentCluster = [];
      clusterEnd = -Infinity;
    }
    currentCluster.push(a);
    clusterEnd = Math.max(clusterEnd, a.item.startHour + a.item.durationHours);
  }
  if (currentCluster.length > 0) clusters.push(currentCluster);

  for (const cluster of clusters) {
    const totalColumns = Math.max(...cluster.map((a) => a.column)) + 1;
    for (const a of cluster) {
      result.set(a.item.id, { column: a.column, totalColumns });
    }
  }

  return result;
}
